import { useEffect, useState, useRef } from 'react';
import { db } from '../../firebase-config';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getMessaging, getToken, onMessage, isSupported as isMessagingSupported } from 'firebase/messaging';
import { getApp } from 'firebase/app';

/**
 * useWorkerRegistration
 *
 * Manages the worker's identity and lifecycle for a logged-in admin user:
 *   1. Generates (or loads from localStorage) a stable workerId (UUID)
 *   2. Creates / updates the `workers/{workerId}` Firestore doc
 *   3. Heartbeats every 30s while the page is visible (lastSeenAt)
 *   4. Registers for FCM and writes the token to the worker doc
 *   5. Listens for foreground push messages (so the page reacts even when
 *      already open)
 *
 * VAPID key is read from REACT_APP_FCM_VAPID_KEY at build time. Provision
 * it in the Firebase console → Project Settings → Cloud Messaging → Web
 * configuration → Generate key pair.
 */

const HEARTBEAT_MS = 30 * 1000;
const WORKER_ID_STORAGE_KEY = 'muuzbox_worker_id';
const DEVICE_LABEL_STORAGE_KEY = 'muuzbox_worker_label';

const getOrCreateWorkerId = () => {
  let id = localStorage.getItem(WORKER_ID_STORAGE_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID && window.crypto.randomUUID())
      || `w_${Math.random().toString(36).slice(2)}_${Date.now()}`;
    localStorage.setItem(WORKER_ID_STORAGE_KEY, id);
  }
  return id;
};

const getDeviceLabel = () => {
  let lbl = localStorage.getItem(DEVICE_LABEL_STORAGE_KEY);
  if (lbl) return lbl;
  const ua = navigator.userAgent || '';
  // Cheap heuristic — phones report enough useful info that this is fine
  // for "which phone is this?" disambiguation in the admin UI.
  const isiOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isMac = /Mac/.test(ua) && !isiOS;
  const isWin = /Windows/.test(ua);
  const base = isiOS ? 'iOS device' : isAndroid ? 'Android device' : isMac ? 'Mac' : isWin ? 'Windows PC' : 'Device';
  lbl = `${base} · ${new Date().toLocaleDateString()}`;
  localStorage.setItem(DEVICE_LABEL_STORAGE_KEY, lbl);
  return lbl;
};

export function useWorkerRegistration(user) {
  const [workerId] = useState(() => getOrCreateWorkerId());
  const [deviceLabel] = useState(() => getDeviceLabel());
  const [error, setError] = useState('');
  const [heartbeatActive, setHeartbeatActive] = useState(false);
  const [lastHeartbeatAt, setLastHeartbeatAt] = useState(null);
  const [fcmStatus, setFcmStatus] = useState('initialising'); // 'initialising' | 'requesting' | 'granted' | 'denied' | 'unsupported' | 'error'
  const [fcmToken, setFcmToken] = useState(null);
  const [fcmError, setFcmError] = useState('');
  const heartbeatTimer = useRef(null);

  // (1) Initial worker doc + heartbeat
  useEffect(() => {
    if (!user?.uid) return;
    const workerRef = doc(db, 'workers', workerId);

    const beat = async () => {
      try {
        await setDoc(workerRef, {
          workerId,
          ownerUid: user.uid,
          ownerEmail: user.email || '',
          deviceLabel,
          lastSeenAt: serverTimestamp(),
          capabilities: ['record', 'upload'],
          userAgent: (navigator.userAgent || '').slice(0, 256),
        }, { merge: true });
        setLastHeartbeatAt(Date.now());
        setHeartbeatActive(true);
      } catch (e) {
        console.warn('[Worker] heartbeat failed:', e);
        setError(e.message || String(e));
        setHeartbeatActive(false);
      }
    };

    beat(); // initial write
    heartbeatTimer.current = setInterval(beat, HEARTBEAT_MS);

    // Pause heartbeat when page is hidden, resume on visible. Saves
    // Firestore writes on a backgrounded tab.
    const onVis = () => {
      if (document.visibilityState === 'visible') beat();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, workerId, deviceLabel]);

  // (2) FCM registration
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const supported = await isMessagingSupported();
        if (!supported) {
          setFcmStatus('unsupported');
          setFcmError('This browser does not support FCM web push (try Chrome on Android, or Safari iOS 16.4+).');
          return;
        }

        setFcmStatus('requesting');
        const perm = await Notification.requestPermission();
        if (cancelled) return;
        if (perm !== 'granted') {
          setFcmStatus('denied');
          setFcmError('Notification permission was not granted. Wake-on-push will not work.');
          return;
        }

        const messaging = getMessaging(getApp());
        const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY;
        if (!vapidKey) {
          setFcmStatus('error');
          setFcmError('REACT_APP_FCM_VAPID_KEY is not set — please configure it.');
          return;
        }

        // The service worker is loaded automatically by getToken() from
        // /firebase-messaging-sw.js at the site root.
        const token = await getToken(messaging, { vapidKey });
        if (cancelled) return;
        if (!token) {
          setFcmStatus('error');
          setFcmError('FCM returned no token.');
          return;
        }
        setFcmToken(token);
        setFcmStatus('granted');

        // Write token to the worker doc so the Cloud Function can target it
        const workerRef = doc(db, 'workers', workerId);
        await setDoc(workerRef, {
          fcmToken: token,
          fcmTokenUpdatedAt: serverTimestamp(),
        }, { merge: true });

        // Foreground message handler — keeps job loop snappy when the
        // PWA happens to be open already (the service worker handles
        // background notifications).
        onMessage(messaging, (payload) => {
          console.log('[Worker] foreground push:', payload);
          // Job loop's onSnapshot will pick up the new doc on its own;
          // this is just a debug log.
        });
      } catch (e) {
        if (cancelled) return;
        setFcmStatus('error');
        setFcmError(e.message || String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [user, workerId]);

  return {
    workerId,
    deviceLabel,
    error,
    heartbeatActive,
    lastHeartbeatAt,
    fcmStatus,
    fcmToken,
    fcmError,
  };
}
